import { PickingInfo } from "@deck.gl/core"
import type { TooltipContent } from "features/map/types"
import store from "integrations/redux/store"
import { get } from "nodash"
import type { LayerId } from "features/map/layers/constants"
import { selectAllNonArchivedFlights } from "features/entity-caching/flights/selectors"
import { selectSelectedMapIcon } from "../control/selectors"
import { getFileNameFromImageId } from "@nova/slice-multifile-upload"
import { selectSelectedFirePerimeterId } from "plugins/DrawPerimeter/FirePerimeterSlice"
import { selectLockedLayersMap } from "panels/content/side-panel/slice.new"

export const hotspotPointHoverHandler = (info: PickingInfo) => {
    const deletedAt = get(info, "object.deletedAt") as string
    const label = deletedAt ? "Deleted" : (get(info, "object.label") as string)

    return {
        html: `<div class="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                    <small>${label}</small>
               </div>`,
        style: {
            background: "",
        },
    } as TooltipContent
}

export const flightPathImagePointHoverHandler = (info: PickingInfo) => {
    const fileName = get(info, "object.fileName") as string
    const id = get(info, "object.imageId") as string
    const label = fileName || getFileNameFromImageId(id)
    return {
        html: `<div class="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                    <small>${label}</small>
               </div>`,
        style: {
            background: "",
        },
    } as TooltipContent
}

export const flightPathsHoverHandler = (info: PickingInfo) => {
    const id = get(info, "object.id") as string
    const state = store.getState()
    const label = get(selectAllNonArchivedFlights(state), [id, "name"])

    if (!label) {
        return null
    }

    return {
        html: `<div class="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                    <small>${label}</small>
               </div>`,
        style: {
            background: "",
        },
    } as TooltipContent
}

export const firePerimeterHoverHandler = (info: PickingInfo) => {
    if (!info.object?.id || selectLockedLayersMap(store.getState())[info.object.id]) return null
    if (info.object.id === selectSelectedFirePerimeterId(store.getState())) {
        return {
            html: `<div class="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                    <small>Click to edit shape</small>
               </div>`,
            style: {
                background: "",
            },
        } as TooltipContent
    } else return null
}

export const lightningStrikeHoverHandler = (info: PickingInfo) => {
    const type = get(info, "object.type") as "CG" | "flash"
    const timeMs = get(info, "object.timeMs") as number
    const lat = get(info, "object.lat") as number
    const lon = get(info, "object.lon") as number

    const title = type === "CG" ? "Confirmed Ground Strike" : "Potential Lightning"
    const when = new Date(timeMs).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        timeZoneName: "short",
    })
    const coords = `${lat.toFixed(4)}, ${lon.toFixed(4)}`

    return {
        html: `<div class="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                    <div class="font-medium">${title}</div>
                    <div class="text-gray-300"><small>${when}</small></div>
                    <div class="text-gray-300"><small>${coords}</small></div>
               </div>`,
        style: {
            background: "",
        },
    } as TooltipContent
}

export const iconsHoverHandler = (info: PickingInfo) => {
    const state = store.getState()
    const activeIcon = selectSelectedMapIcon(state)
    if (activeIcon?.id === info.object.id) {
        return null
    }
    return {
        html: `<div class="text-black font-medium bg-white h-[32px] rounded-lg py-[4px] flex items-center !flex-row pl-2 pr-3 modal-border-gradient text-gray-600">${info.object.name}</div>`,
        style: {
            background: "",
        },
    } as TooltipContent
}

type HoverContentHandlerMap = Record<string, (info: PickingInfo) => TooltipContent>

export const tooltipGenerator =
    (handlerMap: HoverContentHandlerMap) =>
    (info: PickingInfo): TooltipContent => {
        if (!get(info, "object")) {
            return null
        }

        const sourceLayer = get(info, "sourceLayer")
        if (sourceLayer == null) {
            return null
        }

        const layerHandler = get(sourceLayer, "tooltipHandler") as unknown as (
            info: PickingInfo
        ) => TooltipContent

        if (layerHandler) {
            return layerHandler(info)
        }

        const sourceLayerId = get(sourceLayer, "id") as LayerId
        const contentHandler = get(handlerMap, sourceLayerId, () => null)

        return contentHandler(info)
    }
